/**
 * @file `useSigninWithEmailMutation` — POST /api/account/email/authorize.
 * @module @labcraft/lab/ui/hooks/use-signin-with-email
 *
 * Resolves the magic-link mint flow. The mutation's `onSuccess` does
 * NOT touch the `qkMe` cache — the actual session is minted only
 * after the learner clicks the email link, which lands them on
 * `/auth/verify` where `invalidateQueries(qkMe)` refetches.
 *
 * Convention: hooks live in this directory and never call
 * `useMutation` outside it.
 */

import { useMutation } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import { BrowserAcquisition, browserTimezone } from '@labcraft/core/frontend'
import type {
  EmailSigninRequest,
  SentResponse,
} from '@labcraft/account/proxy-routes'
import type { Lab } from '../../client/index.ts'

/**
 * Mint a magic-link sign-in token for `args.email`. Resolves with
 * `{ status: 'sent' }` regardless of whether the address exists —
 * the silent-`sent` shape blocks email-enumeration leaks.
 */
export function useSigninWithEmailMutation() {
  const client = useClient<Lab>()
  return useMutation<SentResponse, Error, EmailSigninRequest>({
    mutationFn: args => {
      const tz = browserTimezone()
      const acq = BrowserAcquisition.read()
      return client.account.signinWithEmail({
        ...args,
        ...(tz ? { timezone: tz } : {}),
        ...(acq?.source ? { acquisitionSource: acq.source } : {}),
        ...(acq?.category ? { acquisitionCategory: acq.category } : {}),
        ...(acq?.campaign ? { acquisitionCampaign: acq.campaign } : {}),
      })
    },
  })
}
